
/**
 * SuccessCelebration Component
 * Animation that shows after successfully subscribing
 */
import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { PartyPopper, X } from "lucide-react";
import confetti from "canvas-confetti";

interface SuccessCelebrationProps {
  onClose: () => void;
  scanCount?: number;
  message?: string;
  subscriptionTier?: 'basic' | 'pro' | null;
}

export function SuccessCelebration({
  onClose,
  scanCount = 0,
  message,
  subscriptionTier = null,
}: SuccessCelebrationProps) {
  const [isVisible, setIsVisible] = useState(true);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const confettiInstanceRef = useRef<confetti.CreateTypes | null>(null);
  const isMountedRef = useRef(true);
  const canvasAddedToDOM = useRef(false);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, 300);
  };

  // Generate appropriate message based on tier
  const getDisplayMessage = () => {
    if (message) return message;
    
    if (subscriptionTier === 'pro') {
      return "You now have unlimited scans with your Pro subscription!";
    } else if (subscriptionTier === 'basic') {
      return `Your Basic subscription gives you ${scanCount} scans per month!`;
    } else {
      return "Thanks for subscribing to PokeScan!";
    }
  };

  // Get scan text display
  const getScanText = () => {
    if (subscriptionTier === 'pro') {
      return "Unlimited Scans";
    } else {
      return `${scanCount} ${scanCount === 1 ? 'Scan' : 'Scans'}`;
    }
  };
  
  // Trigger confetti effect with safer DOM handling
  useEffect(() => {
    // Create a canvas element for confetti
    const canvas = document.createElement("canvas");
    canvasRef.current = canvas;
    
    canvas.style.position = "fixed";
    canvas.style.inset = "0";
    canvas.style.width = "100vw";
    canvas.style.height = "100vh";
    canvas.style.pointerEvents = "none";
    canvas.style.zIndex = "9999";
    canvas.id = "confetti-canvas-" + Date.now(); // Add unique ID
    
    // Safe append - only if document exists
    if (document.body) {
      try {
        document.body.appendChild(canvas);
        canvasAddedToDOM.current = true;
        console.log("Added confetti canvas to DOM");
      } catch (err) {
        console.error("Failed to append canvas to body:", err);
        canvasAddedToDOM.current = false;
      }
    }

    if (canvasAddedToDOM.current) {
      try {
        const myConfetti = confetti.create(canvas, {
          resize: true,
          useWorker: true,
        });
        
        confettiInstanceRef.current = myConfetti;
    
        // Base confetti
        myConfetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 },
          gravity: 1.2,
        });
      } catch (err) {
        console.error("Failed to create confetti instance:", err);
      }
    }

    // Custom confetti colors based on subscription tier
    setTimeout(() => {
      if (!isMountedRef.current) return;
      
      if (confettiInstanceRef.current) {
        const colors = subscriptionTier === 'pro' 
          ? ["#F59E0B", "#FBBF24", "#FFFFFF"] // Gold/yellow for Pro
          : ["#3B82F6", "#1E40AF", "#FFFFFF"]; // Blue for Basic
          
        confettiInstanceRef.current({
          particleCount: 120,
          spread: 100,
          origin: { y: 0.7 },
          colors: colors,
          gravity: 0.8,
        });
      }
    }, 300);

    // Auto-close after a delay
    const closeDelay = 8000; // Extended to 8 seconds
    
    const timer = setTimeout(() => {
      if (isMountedRef.current) {
        handleClose();
      }
    }, closeDelay);

    // Enhanced cleanup function
    return () => {
      isMountedRef.current = false;
      
      clearTimeout(timer);
      
      // Safe removal of the canvas with multiple safety checks
      if (canvasRef.current) {
        try {
          const canvas = canvasRef.current;
          
          // Only try to remove if we successfully added it
          if (canvasAddedToDOM.current && document.body) {
            // Check if the element is still in the DOM before removing
            if (document.getElementById(canvas.id)) {
              document.body.removeChild(canvas);
              console.log("Removed confetti canvas from DOM");
            } else if (canvas.parentNode) {
              canvas.parentNode.removeChild(canvas);
            }
          }
        } catch (error) {
          console.error("Error removing confetti canvas:", error);
        }
      }
      
      // Nullify references
      confettiInstanceRef.current = null;
      canvasRef.current = null;
    };
  }, [subscriptionTier]);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          className="fixed inset-0 flex items-center justify-center z-50 bg-black/50 p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleClose}
        >
          <motion.div
            className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6 relative"
            onClick={(e) => e.stopPropagation()}
            initial={{ scale: 0.9, y: 20, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.9, y: 20, opacity: 0 }}
            transition={{ type: "spring", duration: 0.5 }}
          >
            <Button
              size="icon"
              variant="ghost"
              className="absolute right-2 top-2"
              onClick={handleClose}
            >
              <X size={18} />
            </Button>
            
            <div className="flex flex-col items-center text-center space-y-4">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                subscriptionTier === 'pro' 
                  ? 'bg-yellow-100 text-yellow-600' 
                  : 'bg-blue-100 text-blue-600'
              }`}>
                <PartyPopper className="w-8 h-8" />
              </div>
              
              <h3 className="text-2xl font-bold">
                {subscriptionTier === 'pro' ? 'Pro Subscription Active!' : 'Subscription Successful!'}
              </h3>
              
              <p className="text-gray-600 dark:text-gray-300">
                {getDisplayMessage()}
              </p>
              
              <div className={`mt-4 px-6 py-4 rounded-md w-full ${
                subscriptionTier === 'pro'
                  ? 'bg-yellow-50 text-yellow-700 border border-yellow-200'
                  : 'bg-blue-50 text-blue-700 border border-blue-200'
              }`}>
                <p className="font-semibold text-lg">{getScanText()}</p>
                <p className="text-sm opacity-80">
                  {subscriptionTier === 'pro'
                    ? 'Enjoy your premium access!'
                    : 'Your subscription will renew monthly'}
                </p>
              </div>
              
              <Button
                className={`mt-4 w-full ${
                  subscriptionTier === 'pro'
                    ? 'bg-yellow-500 hover:bg-yellow-600 text-white'
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
                onClick={handleClose}
              >
                Start Scanning
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
